import React, { useState } from 'react';

const About = () => {
  const [activeTab, setActiveTab] = useState('mission');

  const company = {
    founded: "2009",
    headquarters: "Boston, Massachusetts",
    globalPresence: "15+ countries",
    employees: "100+ cybersecurity experts"
  };

  const timeline = [
    {
      year: "2009",
      title: "Foundation",
      description: "Established with a vision to revolutionize cybersecurity"
    },
    {
      year: "2012",
      title: "Global Expansion",
      description: "Expanded operations to Europe and Asia"
    },
    {
      year: "2015",
      title: "AI Integration",
      description: "Pioneered AI-driven security solutions"
    },
    {
      year: "2018",
      title: "Industry Recognition",
      description: "Named Leader in Cybersecurity Services"
    },
    {
      year: "2021",
      title: "Innovation Hub",
      description: "Launched Advanced Security Research Center"
    },
    {
      year: "2024",
      title: "Next Generation",
      description: "Introducing quantum-safe security solutions"
    }
  ];

  const leadership = [
    {
      name: "David Chen",
      role: "Chief Executive Officer",
      description: "20+ years in cybersecurity leadership",
      image: "/api/placeholder/200/200"
    },
    {
      name: "Sarah Williams",
      role: "Chief Technology Officer",
      description: "Former NSA cybersecurity expert",
      image: "/api/placeholder/200/200"
    },
    {
      name: "Michael Rodriguez",
      role: "Chief Security Officer",
      description: "Leading security strategist",
      image: "/api/placeholder/200/200"
    },
    {
      name: "Emily Thompson",
      role: "Head of Research",
      description: "PhD in Computer Science",
      image: "/api/placeholder/200/200"
    }
  ];

  const values = [
    {
      title: "Innovation",
      description: "Pushing boundaries in cybersecurity technology",
      icon: "🔬"
    },
    {
      title: "Excellence",
      description: "Delivering superior security solutions",
      icon: "⭐"
    },
    {
      title: "Integrity",
      description: "Maintaining highest ethical standards",
      icon: "🤝"
    },
    {
      title: "Collaboration",
      description: "Building strong partnerships",
      icon: "🤲"
    }
  ];

  const certifications = [
    "ISO 27001 Certified",
    "NIST Framework Compliant",
    "SOC 2 Type II Certified",
    "HIPAA Compliant"
  ];

  return (
    <div className="pt-16 min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-purple-700 to-blue-500 text-white py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">About Grace Technologies</h1>
            <p className="text-xl mb-8">
              Pioneering the future of cybersecurity with innovation, expertise, and unwavering commitment to excellence
            </p>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex overflow-x-auto">
            {['mission', 'journey', 'leadership', 'values'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-4 text-sm font-medium border-b-2 whitespace-nowrap ${
                  activeTab === tab
                    ? 'border-purple-700 text-purple-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content Sections */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Mission Section */}
        <div className={activeTab === 'mission' ? 'block' : 'hidden'}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">Our Mission</h2>
              <p className="text-gray-600 mb-6">
                At Grace Technologies, we're on a mission to revolutionize cybersecurity through innovation and excellence. 
                We believe in creating a secure digital future where organizations can thrive without fear of cyber threats.
              </p>
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="text-xl font-semibold mb-4">Company Overview</h3>
                <div className="space-y-3">
                  <p className="flex justify-between">
                    <span className="text-gray-600">Founded:</span>
                    <span className="font-medium">{company.founded}</span>
                  </p>
                  <p className="flex justify-between">
                    <span className="text-gray-600">Headquarters:</span>
                    <span className="font-medium">{company.headquarters}</span>
                  </p>
                  <p className="flex justify-between">
                    <span className="text-gray-600">Global Presence:</span>
                    <span className="font-medium">{company.globalPresence}</span>
                  </p>
                  <p className="flex justify-between">
                    <span className="text-gray-600">Team:</span>
                    <span className="font-medium">{company.employees}</span>
                  </p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              {values.map((value, index) => (
                <div key={index} className="bg-white p-6 rounded-xl shadow-lg">
                  <div className="text-4xl mb-4">{value.icon}</div>
                  <h3 className="text-xl font-semibold mb-2">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Journey Section */}
        <div className={activeTab === 'journey' ? 'block' : 'hidden'}>
          <h2 className="text-3xl font-bold mb-12 text-center">Our Journey</h2>
          <div className="space-y-12">
            {timeline.map((event, index) => (
              <div key={index} className="flex items-center gap-8">
                <div className="w-32 text-right">
                  <div className="text-2xl font-bold text-purple-700">{event.year}</div>
                </div>
                <div className="relative">
                  <div className="w-4 h-4 rounded-full bg-purple-700"></div>
                  <div className="absolute top-0 left-2 h-full w-0.5 bg-purple-200"></div>
                </div>
                <div className="flex-1 bg-white p-6 rounded-xl shadow-lg">
                  <h3 className="text-xl font-semibold mb-2">{event.title}</h3>
                  <p className="text-gray-600">{event.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Leadership Section */}
        <div className={activeTab === 'leadership' ? 'block' : 'hidden'}>
          <h2 className="text-3xl font-bold mb-12 text-center">Our Leadership Team</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {leadership.map((member, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden">
                <img 
                  src={member.image} 
                  alt={member.name}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-xl font-semibold mb-1">{member.name}</h3>
                  <p className="text-purple-700 mb-2">{member.role}</p>
                  <p className="text-gray-600">{member.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Values Section */}
        <div className={activeTab === 'values' ? 'block' : 'hidden'}>
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-6">Our Values & Certifications</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We maintain the highest standards of security and compliance
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold mb-6">Core Values</h3>
              <div className="space-y-6">
                {values.map((value, index) => (
                  <div key={index} className="bg-white p-6 rounded-xl shadow-lg">
                    <div className="flex items-center gap-4">
                      <div className="text-4xl">{value.icon}</div>
                      <div>
                        <h4 className="text-xl font-semibold mb-2">{value.title}</h4>
                        <p className="text-gray-600">{value.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-6">Certifications</h3>
              <div className="bg-white p-8 rounded-xl shadow-lg">
                <div className="grid grid-cols-1 gap-6">
                  {certifications.map((cert, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                      <span className="font-medium">{cert}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;